import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlSegment } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@auth0/auth0-angular';
import { ToastrService } from 'ngx-toastr';
import { EMPTY } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { UserService } from '../services/user.service';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe } from 'rxjs';
import { LoadingService } from '../services/loading.service';
import { SigninService } from '../services/sign-in.service';
import { FormGroup } from '@angular/forms';

/**
 * Registration store that manages the entire registration process state
 * including multi-step form progression, user validation, and profile creation.
 */

// State interface definition
type RegistrationStoreState = {
  accessToken: string | null; // Auth0 access token
  imageFile: File | null; // User's profile image file
  currentStep: 'password' | 'personal-info' | 'finished-register'; // Current registration step
  alreadyExistNick: string | null; // Existing username check
  finishRegister: boolean; // Registration completion flag
  form: any | null; // Registration form data
  imageUrl: string | null; // Profile image preview URL
  fileImage: File | null; // Processed profile image file
  isPasswordVisible: boolean; // Password field visibility toggle
  isWhcmsSignup: boolean;
  isDomainOwner: boolean;
  email: string;
  firstName: string;
  lastName: string;
  publicName: string;
};

// Initial store state
const initialState: RegistrationStoreState = {
  accessToken: null,
  imageFile: null,
  currentStep: 'password',
  alreadyExistNick: null,
  finishRegister: false,
  form: null,
  imageUrl: null,
  fileImage: null,
  isPasswordVisible: false,
  isWhcmsSignup: false,
  isDomainOwner: false,
  email: '',
  firstName: '',
  lastName: '',
  publicName: '',
};

export const RegistrationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      router = inject(Router),
      toastr = inject(ToastrService),
      userService = inject(UserService),
      translate = inject(TranslateService),
      loadingService = inject(LoadingService),
      signInService = inject(SigninService),
      auth0 = inject(AuthService),
    ) => {
      /**
       * Validates if a username already exists
       * Proceeds to next step if username is available
       */
      const checkIfUserExists = rxMethod<string>(
        pipe(
          switchMap((nick) =>
            userService.userIsValidated(nick).pipe(
              tap((exists) => {
                if (exists) {
                  toastr.error(translate.instant('REGISTER.nickAlreadyExist'));
                } else {
                  patchState(store, { currentStep: 'personal-info' });
                }
              }),
              catchError((error) => {
                console.error(error);
                return EMPTY;
              }),
            ),
          ),
        ),
      );

      /**
       * Formats terms acceptance value
       */
      function setTerms(form: any): void {
        if (form.controls['terms'].value) {
          form.controls['terms'].setValue('true');
        }
      }

      /**
       * Creates Auth0 signup options object
       */
      function getSignupOptions(form: any) {
        console.log(form);
        return {
          connection: 'Username-Password-Authentication',
          email: form.form.controls['nick'].value,
          password: form.form.controls['password'].value,
          scope: 'openid profile',
          userMetadata: {
            lang: navigator.language || 'en',
          },
        };
      }

      /**
       * Updates current registration step
       */
      const setCurrentStep = (step: 'password' | 'personal-info') => {
        patchState(store, { currentStep: step });
      };

      /**
       * Handles profile picture upload and preview
       */
      const updateProfilePicture = (file: any): void => {
        // Create a blob URL from the File object
        const blobUrl = URL.createObjectURL(file);
        patchState(store, {
          imageUrl: blobUrl,
          fileImage: file,
        });
      };

      /**
       * Initiates SSO authentication flow
       */
      const SSOAuth = (provider: string) => {
        auth0.loginWithRedirect({
          authorizationParams: {
            connection: provider,
            redirect_uri: `${window.location.origin}/overview`,
            ui_locales: navigator.language || 'en',
          },
        });
      };

      /**
       * Toggles password field visibility
       */
      const togglePasswordVisibility = () => {
        patchState(store, {
          isPasswordVisible: !store.isPasswordVisible(),
        });
      };

      /**
       * Validates and sets email components
       * Redirects to home if validation fails
       */
      const setAndTestEmail = (email: string): void => {
        const formattedEmail = email.replace(/\s+/g, '+');
        const [user, domain] = formattedEmail.split('@');
        const form = store.form();

        if (!form) return;

        // Update form controls
        form.get('nick')?.setValue(email);
        form.get('user')?.setValue(user);
        form.get('domain')?.setValue(domain);
        form.get('nick')?.markAsTouched();

        // Validate and redirect if invalid
        if (!form.get('domain')?.valid || !form.get('user')?.valid) {
          router.navigateByUrl('/home');
          return;
        }

        // Update store state
        patchState(store, {
          form,
          alreadyExistNick: formattedEmail,
          email: formattedEmail,
        });
      };

      /**
       * Submits the registration form
       */
      const submitForm = (form: FormGroup) => {
        loadingService.setLoading(true);

        // Handle reserved shortId if exists
        const reservedShortid = localStorage.getItem('reservedShortid');
        if (reservedShortid) {
          userService
            .callReservedShortid(reservedShortid, form.controls['nick'].value)
            .then(() => {
              toastr.success(translate.instant('LOGIN.shortIdPreregistered'));
              localStorage.removeItem('reservedShortid');
            })
            .catch(() => {
              toastr.error(translate.instant('LOGIN.shortIdPreregisteredFailed'));
            });
        }

        // Attempt user registration
        return signInService
          .WebAuth(form, form.controls['isDomainOwner'].value, store.fileImage())
          .pipe(
            tap((response: any) => {
              loadingService.setLoading(false);
              if (response === 'user already') {
                toastr.error(translate.instant('REGISTER.nickAlreadyExist'));
                patchState(store, {
                  alreadyExistNick: form.controls['nick'].value,
                });
              }
              if (response === 'user success') {
                toastr.success(translate.instant('REGISTER.registrationSuccessful'), '', { timeOut: 3000 });
                patchState(store, {
                  finishRegister: true,
                  currentStep: 'finished-register',
                  form: form,
                });
              }
            }),
            catchError((error) => {
              loadingService.setLoading(false);
              console.error('Registration error:', error);
              return EMPTY;
            }),
          )
          .subscribe();
      };

      const initializeFromUrl = (route: ActivatedRouteSnapshot) => {
        const isActivateAccount = route.url.some((segment: UrlSegment) => segment.path === 'activate-account');

        const { name = '', email = '' } = route.queryParams;
        const nameParts = name.trim().replace(/\s+/g, ' ').split(' ');

        const firstName = nameParts[0] || '';
        const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
        const publicName = nameParts.join(' ');

        patchState(store, {
          isWhcmsSignup: isActivateAccount,
          isDomainOwner: isActivateAccount,
          email,
          firstName,
          lastName,
          publicName,
        });
      };

      const setUserDetails = (params: { email?: string; name?: string }) => {
        if (params.name?.trim()) {
          const nameParts = params.name.replace(/\s+/g, '+').split('+');
          patchState(store, {
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
          });
        }

        if (typeof params.email === 'string' && params.email.trim()) {
          const formattedEmail = params.email.replace(/\s+/g, '+');
          patchState(store, { email: formattedEmail });
        }
      };

      const updatePublicName = (form: FormGroup): boolean => {
        // Check if user has manually edited the publicName
        if (form.get('publicName')?.touched && form.get('publicName')?.dirty) return false;

        const firstName = form.get('firstName')?.value?.trim() || '';
        const lastName = form.get('lastName')?.value?.trim() || '';
        const publicName = [firstName.split(' ')[0], lastName.split(' ').slice(-1)[0]].filter(Boolean).join(' ');

        patchState(store, { publicName });
        form.get('publicName')?.markAsUntouched();
        form.get('publicName')?.markAsPristine();
        return true;
      };

      return {
        setCurrentStep,
        updateProfilePicture,
        checkIfUserExists,
        SSOAuth,
        togglePasswordVisibility,
        setAndTestEmail,
        submitForm,
        initializeFromUrl,
        setUserDetails,
        updatePublicName,
      };
    },
  ),
);
